<script setup lang="ts">
import {
  cardParticles,
  hexAlpha,
  mixColor,
  type LevelTier,
} from '@/utils/levels';

// A "pele" de um elo: borda viva que corre, fundo na cor do elo, brilho de canto
// e as partículas pulsando atrás do conteúdo. Vive num componente porque o
// cartão do jogador (desktop e mobile) e a faixa do dropdown do header pedem o
// mesmo tratamento, e a terceira cópia disso ia divergir.
//
// Quem usa entra com o conteúdo pelo slot e escolhe raio e espessura da borda.
const props = withDefaults(
  defineProps<{
    tier: LevelTier;
    /** Raio externo em px — o interno é derivado. */
    radius?: number;
    /** Espessura da borda viva em px. */
    stroke?: number;
    /**
     * Marca d'água do emblema na direita. Desligue em faixas muito baixas
     * (ex.: a do header do jogo, 26px), onde só um recorte dela aparece e
     * vira mancha em vez de textura.
     */
    showWatermark?: boolean;
  }>(),
  { radius: 12, stroke: 1, showWatermark: true },
);

const strokeGradient = computed(
  () =>
    `linear-gradient(100deg,rgba(255,255,255,.1) 0%,#D8DEE9 12%,${props.tier.color} 30%,rgba(255,255,255,.14) 46%,${props.tier.accent} 62%,#EAEEF5 78%,rgba(255,255,255,.1) 100%)`,
);

// Mesmas paradas do cartão do jogador do desktop (protótipo `me.bg`): antes a
// pele usava misturas mais fundas (.3/.62/.88) e a faixa do dropdown saía
// visivelmente mais escura que o cartão da página de níveis.
const surface = computed(
  () =>
    `linear-gradient(168deg,${mixColor(props.tier.color, '#05091A', 0.2)} 0%,${mixColor(
      props.tier.color,
      '#05091A',
      0.58,
    )} 46%,${mixColor(props.tier.color, '#05091A', 0.9)} 100%)`,
);

const particles = computed(() => cardParticles(props.tier));
</script>

<template>
  <div
    class="levels-skin relative"
    :style="{
      borderRadius: `${radius}px`,
      padding: `${stroke}px`,
      background: strokeGradient,
      backgroundSize: '280% 100%',
      boxShadow: `0 14px 30px -24px ${hexAlpha(tier.color, 0.7)}`,
    }"
  >
    <div
      class="relative"
      :style="{
        borderRadius: `${radius - stroke}px`,
        background: surface,
      }"
    >
      <!--
        `overflow: clip` e não `hidden`: `hidden` cria contexto de rolagem, e num
        painel que sobe isso vira scroll fantasma quando algo recebe foco.
      -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 [overflow:clip]"
        :style="{ borderRadius: `${radius - stroke}px` }"
      >
        <div
          class="absolute inset-0"
          style="
            background: radial-gradient(
              120% 100% at 6% 0%,
              rgba(255, 255, 255, 0.06),
              transparent 58%
            );
          "
        ></div>
        <span
          v-for="(particle, index) in particles"
          :key="index"
          class="levels-skin-pop absolute rounded-full"
          :style="{
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 ${particle.blur} ${particle.color}`,
            animationDuration: particle.duration,
            animationDelay: particle.delay,
          }"
        ></span>
        <!-- Marca d'água do emblema, centralizada e grande — a mesma textura do
             cartão do jogador da página de níveis. Por cima das partículas,
             como lá. `padding-top` em % cria o quadrado a partir da largura:
             `aspect-square` não gera CSS neste projeto. -->
        <span
          v-if="showWatermark"
          class="absolute left-1/2 top-1/2 block w-[104%] -translate-x-1/2 -translate-y-1/2 bg-contain bg-center bg-no-repeat opacity-[0.05]"
          :style="{
            backgroundImage: `url(${tier.badge})`,
            paddingTop: '104%',
          }"
        ></span>
      </div>

      <div class="relative">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* O reset global mata `transition`, então tudo aqui anda por keyframes. */
@keyframes levelsSkinStroke {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 280% 50%;
  }
}

.levels-skin {
  animation: levelsSkinStroke 7s linear infinite;
}

@keyframes levelsSkinPop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px) scale(0.6);
  }
  45% {
    opacity: 0.9;
    transform: translateY(-4px) scale(1);
  }
}

.levels-skin-pop {
  opacity: 0;
  animation-name: levelsSkinPop;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
</style>
